import AddPolicyModal from '../AddPolicyModal'
import {useAuth} from '../../../../modules/auth'
import {useEffect, useState} from 'react'
import db from '../../../../../services/firebase'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import UpgradePlan from 'app/components/UpgradePlan'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ExcelJS from 'exceljs'
import {saveAs} from 'file-saver'
import Button from 'app/components/Button'

const PolicyListToolbar = ({policies, currentAuditContext}) => {
  const {currentUser} = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [isDropdownVisible, setDropdownVisible] = useState(false)

  useEffect(() => {
    getActiveFramworks()
  }, [])

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleExportAll = async () => {
    const frameworkToFileUrl = {
      'SOC 2': '/templates/SOC_2 Policies.zip',
      'ISO 27001:2022': '/templates/ISO 27001 Policies.zip',
    }

    activeFrameworks.forEach((framework) => {
      const fileUrl = frameworkToFileUrl[framework]
      if (fileUrl) {
        const link = document.createElement('a')
        link.href = fileUrl
        link.setAttribute('download', '')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        showErrorToast({message: `Export not available for framework: ${framework}`})
      }
    })
  }

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Policies')

    worksheet.views = [{state: 'frozen', ySplit: 1}]

    worksheet.columns = [
      {header: 'Policy Name', key: 'policy', width: 50},
      {header: 'Framework', key: 'frameworks', width: 30},
      {header: 'Owner', key: 'owner', width: 30},
      {header: 'Status', key: 'status', width: 15},
      {header: 'Last Approved', key: 'last_approved', width: 20},
      {header: 'Review Date', key: 'review_date', width: 20},
      {header: 'Timeframe', key: 'timeframe', width: 15},
    ]

    const headerRow = worksheet.getRow(1)
    headerRow.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 10}
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {argb: 'FF000000'},
    }
    headerRow.height = 30
    headerRow.alignment = {horizontal: 'center'}

    for (let i = 1; i <= 8; i++) {
      const cell = headerRow.getCell(i)
      cell.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 11}
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {argb: 'FF000000'},
      }
      cell.alignment = {horizontal: 'center', vertical: 'middle'}
    }

    // Filter policies based on active frameworks
    const filteredPolicies = policies.filter((policy) => {
      if (!policy.frameworks || !policy.frameworks.length) return false
      return policy.frameworks.some((framework) => activeFrameworks.includes(framework))
    })

    const exportData = filteredPolicies.map((row) => ({
      policy: row.policy || '',
      frameworks: (row.frameworks || [])
        .filter((framework) => activeFrameworks.includes(framework))
        .join(', '),
      owner: row.owner || '',
      status: row.status || '',
      last_approved: row.last_approved ? new Date(row.last_approved).toLocaleDateString() : '',
      review_date: row.review_date || '',
      timeframe: row.timeframe || '',
    }))

    if (exportData.length === 0) {
      showErrorToast({message: 'No policies found for active frameworks'})
      return
    }

    exportData.forEach((item) => {
      const row = worksheet.addRow(item)
      row.height = 20
      row.font = {size: 10}

      if (item.status === 'Deactivated') {
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'DDEEFF'},
          }
        })
      } else if (item.status === 'Approved') {
        row.getCell('E').fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {argb: 'D9EAD3'},
        }
      } else if (item.status === 'Incomplete') {
        row.getCell('E').fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {argb: 'F4CCCC'},
        }
      }
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const excelBlob = new Blob([buffer], {type: 'application/octet-stream'})

    saveAs(excelBlob, `policies.xlsx`)
    showSuccessToast({message: 'Policies data exported successfully'})
  }

  return (
    <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
      <AddPolicyModal isOpen={isModalOpen} onClose={handleCloseModal} />
      {activeFrameworks.length > 0 && (
        <div className='dropdown'>
          {(currentUser?.role !== 'Auditor' && !currentAuditContext) && (
            <Button
              className='me-4'
              onClick={() => toggleDropdown()}
              data-bs-toggle='dropdown'
              id='dropdownMenu2'
              aria-haspopup='true'
              aria-expanded='false'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-download me-2'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
                <path d='M7 11l5 5l5 -5' />
                <path d='M12 4l0 12' />
              </svg>
              <span className='me-2'>Export </span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-down rotate-180'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M6 9l6 6l6 -6' />
              </svg>
            </Button>
          )}
          <ul
            className='dropdown-menu mt-2'
            aria-labelledby='dropdownMenu2'
            style={{width: '160px'}}
          >
            <li>
              <button
                className='dropdown-item text-hover-primary button-with-icon'
                type='button'
                style={{color: '#262626'}}
                onClick={() =>
                  currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? setShowModal(true) : handleExportAll()
                }
                disabled={currentUser?.role === 'Auditor'}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#5A5A5A'
                  stroke-width='2'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-pencil mb-1 mx-1 '
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4' />
                  <path d='M13.5 6.5l4 4' />
                </svg>{' '}
                Export Templates
              </button>
            </li>

            <li>
              <button
                className='dropdown-item text-hover-primary button-with-icon'
                type='button'
                style={{color: '#262626'}}
                onClick={() =>
                  currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? setShowModal(true) : handleExportExcel()
                }
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#5A5A5A'
                  stroke-width='2'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-file-import mb-1 mx-1'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M14 3v4a1 1 0 0 0 1 1h4' />
                  <path d='M5 13v-8a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2h-5.5m-9.5 -2h7m-3 -3l3 3l-3 3' />
                </svg>{' '}
                Export excel report
              </button>
            </li>
          </ul>
        </div>
      )}
      {(currentUser?.role !== 'Auditor' && !currentAuditContext) && (
        <Button
          btnType='primary'
          onClick={() =>
            currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? setShowModal(true) : handleOpenModal()
          }
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            stroke-width='2'
            stroke-linecap='round'
            stroke-linejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-plus me-2'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M12 5l0 14' />
            <path d='M5 12l14 0' />
          </svg>
          Add Policy
        </Button>
      )}
      <UpgradePlan show={showModal} onClose={() => setShowModal(false)} />
    </div>
  )
}

export {PolicyListToolbar}
